import { useId, useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { EntitySelect } from "@components/entity/EntitySelect";
import { Entity } from "@common/types/entity";
import { HStack } from "@design-system/HStack/HStack";
import { ScheduleApplyFormValues } from "../scheduleApplyFormHelpers";
import { DrawerInlineRow } from "./DrawerInlineRow";
import { ScheduleManualLabeledField } from "./ScheduleManualLabeledField";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import drawerStyles from "../ScheduleDrawer.module.scss";

const fields = createFormikFields<ScheduleApplyFormValues>();

const SOURCE_LABEL_PLACEHOLDER = "Annual contract";

interface ScheduleManualApplyDetailsProps {
    entityIds: number[];
    entitiesById: Map<number, Entity>;
    layout: ScheduleManualLayout;
}

/** The source label, plus the entity when the policy spans several. */
export const ScheduleManualApplyDetails = ({
    entityIds,
    entitiesById,
    layout,
}: ScheduleManualApplyDetailsProps) => {
    const sourceLabelId = useId();
    const { values, setFieldValue } =
        useFormikContext<ScheduleApplyFormValues>();
    const entities = useMemo(
        () =>
            entityIds.flatMap((id) => {
                const entity = entitiesById.get(id);
                return entity ? [entity] : [];
            }),
        [entityIds, entitiesById],
    );
    const selectedEntity =
        values.entityId == null ? undefined : entitiesById.get(values.entityId);
    const hasEntityPicker = entityIds.length > 1;
    const entityPicker = (
        <EntitySelect
            entities={entities}
            selectedEntity={selectedEntity}
            onChange={(entity) =>
                void setFieldValue("entityId", entity?.id ?? null)
            }
            triggerButtonProps={{
                variant: "primary",
                size: "md",
                "aria-label": "Entity",
            }}
        />
    );

    if (layout === "drawer") {
        return (
            <div
                className={classNames(
                    drawerStyles.card,
                    drawerStyles.cardStack,
                )}
            >
                <DrawerInlineRow label="Source label" labelId={sourceLabelId}>
                    <div className={drawerStyles.rowInput}>
                        <fields.Text
                            name="manualSourceLabel"
                            aria-labelledby={sourceLabelId}
                            placeholder={SOURCE_LABEL_PLACEHOLDER}
                        />
                    </div>
                </DrawerInlineRow>
                {hasEntityPicker && (
                    <DrawerInlineRow label="Entity">
                        {entityPicker}
                    </DrawerInlineRow>
                )}
            </div>
        );
    }

    return (
        <HStack gap={12} align="end" wrap>
            <fields.Text
                name="manualSourceLabel"
                label="Source label"
                placeholder={SOURCE_LABEL_PLACEHOLDER}
            />
            {hasEntityPicker && (
                <ScheduleManualLabeledField label="Entity">
                    {entityPicker}
                </ScheduleManualLabeledField>
            )}
        </HStack>
    );
};
